import { ref, computed } from 'vue'
import { useApi } from './useApi'

export function useIdeaStore(csrfToken) {
  const api = useApi(csrfToken)

  const ideas = ref([])
  const loading = ref(false)
  const statusFilter = ref('fresh')
  const searchQuery = ref('')

  const filteredIdeas = computed(() => {
    let result = ideas.value
    if (statusFilter.value && statusFilter.value !== 'all') {
      result = result.filter(i => i.status === statusFilter.value)
    }
    if (searchQuery.value.trim()) {
      const q = searchQuery.value.toLowerCase()
      result = result.filter(i => i.body.toLowerCase().includes(q))
    }
    return result
  })

  const freshCount = computed(() => ideas.value.filter(i => i.status === 'fresh').length)

  async function loadIdeas() {
    loading.value = true
    try {
      ideas.value = await api.get('/api/ideas')
    } finally {
      loading.value = false
    }
  }

  async function createIdea(body) {
    const idea = await api.post('/api/ideas', { idea: { body } })
    ideas.value.unshift(idea)
    return idea
  }

  async function batchCreate(bodies) {
    const created = await api.post('/api/ideas/batch_create', { bodies })
    ideas.value.unshift(...created)
    return created
  }

  async function updateIdea(id, params) {
    const updated = await api.patch(`/api/ideas/${id}`, { idea: params })
    const idx = ideas.value.findIndex(i => i.id === id)
    if (idx !== -1) ideas.value[idx] = updated
    return updated
  }

  async function deleteIdea(id) {
    await api.delete(`/api/ideas/${id}`)
    ideas.value = ideas.value.filter(i => i.id !== id)
  }

  async function archiveIdea(id) {
    return updateIdea(id, { status: 'archived' })
  }

  async function useAsKnowledgeItem(ideaId, projectId) {
    const result = await api.post(`/api/ideas/${ideaId}/use_as_knowledge_item`, {
      project_id: projectId,
    })
    const idx = ideas.value.findIndex(i => i.id === ideaId)
    if (idx !== -1) ideas.value[idx] = result.idea
    return result
  }

  async function useAsPublicationEntry(ideaId, publicationId) {
    const result = await api.post(`/api/ideas/${ideaId}/use_as_publication_entry`, {
      publication_id: publicationId,
    })
    const idx = ideas.value.findIndex(i => i.id === ideaId)
    if (idx !== -1) ideas.value[idx] = result.idea
    return result
  }

  return {
    ideas,
    loading,
    statusFilter,
    searchQuery,
    filteredIdeas,
    freshCount,
    loadIdeas,
    createIdea,
    batchCreate,
    updateIdea,
    deleteIdea,
    archiveIdea,
    useAsKnowledgeItem,
    useAsPublicationEntry,
  }
}
